import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  FaWhatsapp,
  FaLinkedinIn,
  FaArrowLeft,
  FaArrowRight,
  FaClock,
} from "react-icons/fa";
import "./blogDetail.css";

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  const baseImagePath = "https://dashboard.jyotirvani.com/uploads/blog/";

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        const response = await fetch(
          "https://dashboard.jyotirvani.com/api/blogdetails",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ blogid: id }),
          }
        );

        const data = await response.json();

        if (data.status && data.data) {
          setBlog(data.data);
        } else {
          console.error("Blog fetch failed:", data.message);
        }
      } catch (error) {
        console.error("API error:", error);
      }
    };

    const fetchAllBlogs = async () => {
      try {
        const response = await fetch(
          "https://dashboard.jyotirvani.com/api/fetchblog"
        );
        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          setAllBlogs(data.data);
        } else {
          console.error("All blogs fetch failed:", data.message);
        }
      } catch (error) {
        console.error("Fetch all blogs error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetail();
    fetchAllBlogs();
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html || "") };
  };

  const stripHtml = (html) => {
    return html ? html.replace(/<[^>]+>/g, "") : "";
  };

  const calculateReadingTime = (html) => {
    const text = stripHtml(html);
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  };

  const sortedBlogs = useMemo(() => {
    return [...allBlogs].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [allBlogs]);

  const currentIndex = sortedBlogs.findIndex((b) => String(b.id) === String(id));
  const prevBlog = currentIndex > 0 ? sortedBlogs[currentIndex - 1] : null;
  const nextBlog =
    currentIndex >= 0 && currentIndex < sortedBlogs.length - 1
      ? sortedBlogs[currentIndex + 1]
      : null;

  const relatedBlogs = useMemo(() => {
    if (!blog) return [];

    const matched = sortedBlogs.filter(
      (item) =>
        String(item.id) !== String(blog.id) &&
        item.category?.toLowerCase() === blog.category?.toLowerCase()
    );

    if (matched.length > 0) return matched.slice(0, 3);

    return sortedBlogs
      .filter((item) => String(item.id) !== String(blog.id))
      .slice(0, 3);
  }, [blog, sortedBlogs]);

  const currentUrl = window.location.href;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${blog?.title || "Check this blog"} - ${currentUrl}`
  )}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    currentUrl
  )}`;

  if (loading) return <div className="loading">Loading...</div>;
  if (!blog) return <div className="loading">Blog not found</div>;

  return (
    <>
      <div
        className="reading-progress-bar"
        style={{ width: `${scrollProgress}%` }}
      ></div>

      <div className="blog-detail-page-wrap">
        

        <div className="ra-detail-blog-page">
          <h1 className="blog-title">{blog.title}</h1>

          <div className="blog-top-meta">
            <p className="blog-date">
              {new Date(blog.created_at).toLocaleDateString()}
            </p>
            <p className="blog-reading-time">
              <FaClock /> {calculateReadingTime(blog.description)}
            </p>
          </div>

          <img
            src={`${baseImagePath}${blog.image}`}
            alt={blog.title}
            className="blog-detail-image "
          />

         <div
  className="blog-html-content"
  dangerouslySetInnerHTML={createMarkup(blog.description)}
/>

<div className="share-blog-section">
  <h3 className="share-blog-title">Share this blog</h3>
  <div className="share-blog-buttons">
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noreferrer"
      title="Share on WhatsApp"
      className="share-btn whatsapp-btn"
    >
      <FaWhatsapp /> WhatsApp
    </a>

    <a
      href={linkedinUrl}
      target="_blank"
      rel="noreferrer"
      title="Share on LinkedIn"
      className="share-btn linkedin-btn"
    >
      <FaLinkedinIn /> LinkedIn
    </a>
  </div>
</div>
          

          <div className="blog-navigation">
            {prevBlog ? (
              <Link to={`/blog/${prevBlog.id}`} className="nav-btn">
                <FaArrowLeft /> Previous
              </Link>
            ) : (
              <div></div>
            )}

            {nextBlog ? (
              <Link to={`/blog/${nextBlog.id}`} className="nav-btn">
                Next <FaArrowRight />
              </Link>
            ) : null}
          </div>

          {relatedBlogs.length > 0 && (
            <div className="related-blogs-section">
              <h2 className="related-heading">Recent Blogs</h2>

              <div className="related-blogs-grid">
                {relatedBlogs.map((item) => (
                  <div key={item.id} className="related-blog-card">
                    <img
                      src={`${baseImagePath}${item.image}`}
                      alt={item.title}
                      className="related-blog-image"
                    />
                    <div className="related-blog-content">
                      <p className="related-blog-date">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                      <h3 className="related-blog-title">{item.title}</h3>
                      <p className="related-blog-excerpt">
                        {stripHtml(item.description).slice(0, 90)}...
                      </p>
                      <Link to={`/blog/${item.id}`} className="related-read-more">
                        Read More
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogDetail;